import { ApolloClient, gql } from '@apollo/client';
import { cellToString, normalizeHeaderLabel } from './import-column-mapper';

type NamedLookup = { id: number; name: string };
type SubregionLookup = { id: number; name: string; regionId?: number };

export type ImportLookupCache = {
  categories: Map<string, NamedLookup>;
  regions: Map<string, NamedLookup>;
  subregions: Map<string, SubregionLookup>;
  wasteCategories: Map<string, NamedLookup>;
};

function putByName<T extends { name: string }>(map: Map<string, T>, rec: T): void {
  const key = normalizeHeaderLabel(rec.name);
  if (!key || map.has(key)) return;
  map.set(key, rec);
}

async function loadNamedList(
  client: ApolloClient<object>,
  query: ReturnType<typeof gql>,
  dataKey: string,
): Promise<any[]> {
  const ret = await client.query({
    query,
    variables: { limit: 5000, offset: 0 },
    fetchPolicy: 'network-only',
  });
  const data = ret?.data?.[dataKey]?.data;
  return Array.isArray(data) ? data : [];
}

/** Φορτώνει lookups μία φορά για όλη την εισαγωγή */
export async function loadImportLookupCache(
  client: ApolloClient<object>,
): Promise<ImportLookupCache> {
  const [categories, regions, subregions, wasteCategories] = await Promise.all([
    loadNamedList(
      client,
      gql`
        query getCollectionPointCategories($limit: Int, $offset: Int) {
          getCollectionPointCategories(limit: $limit, offset: $offset, order: "name ASC") {
            data {
              id
              name
            }
          }
        }
      `,
      'getCollectionPointCategories',
    ),
    loadNamedList(
      client,
      gql`
        query getRegions($limit: Int, $offset: Int) {
          getRegions(limit: $limit, offset: $offset, order: "name ASC") {
            data {
              id
              name
            }
          }
        }
      `,
      'getRegions',
    ),
    loadNamedList(
      client,
      gql`
        query getSubregions($limit: Int, $offset: Int) {
          getSubregions(limit: $limit, offset: $offset, order: "name ASC") {
            data {
              id
              name
              regionId
            }
          }
        }
      `,
      'getSubregions',
    ),
    loadNamedList(
      client,
      gql`
        query getCpWasteCategories($limit: Int, $offset: Int) {
          getCpWasteCategories(limit: $limit, offset: $offset, order: "name ASC") {
            data {
              id
              name
            }
          }
        }
      `,
      'getCpWasteCategories',
    ),
  ]);

  const cache: ImportLookupCache = {
    categories: new Map(),
    regions: new Map(),
    subregions: new Map(),
    wasteCategories: new Map(),
  };

  for (const rec of categories) putByName(cache.categories, rec);
  for (const rec of regions) putByName(cache.regions, rec);
  for (const rec of subregions) putByName(cache.subregions, rec);
  for (const rec of wasteCategories) putByName(cache.wasteCategories, rec);

  return cache;
}

function findByName<T>(map: Map<string, T>, value: unknown): T | undefined {
  const key = normalizeHeaderLabel(cellToString(value) ?? '');
  if (!key) return undefined;
  return map.get(key);
}

export function resolveCategoryId(cache: ImportLookupCache, value: unknown): number | undefined {
  return findByName(cache.categories, value)?.id;
}

export function resolveRegion(
  cache: ImportLookupCache,
  value: unknown,
): NamedLookup | undefined {
  return findByName(cache.regions, value);
}

export function resolveSubregion(
  cache: ImportLookupCache,
  value: unknown,
): SubregionLookup | undefined {
  return findByName(cache.subregions, value);
}

/** Ονόματα κατηγοριών αποβλήτων χωρισμένα με κόμμα ή ερωτηματικό */
export function resolveWasteCategoryIds(
  cache: ImportLookupCache,
  value: unknown,
): { ids: number[]; missing: string[] } {
  const raw = cellToString(value);
  if (!raw) return { ids: [], missing: [] };

  const parts = raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const ids: number[] = [];
  const missing: string[] = [];
  for (const part of parts) {
    const rec = findByName(cache.wasteCategories, part);
    if (!rec) missing.push(part);
    else if (!ids.includes(rec.id)) ids.push(rec.id);
  }
  return { ids, missing };
}

export function parseYesNo(value: unknown): boolean | undefined {
  const raw = cellToString(value);
  if (!raw) return undefined;
  const s = normalizeHeaderLabel(raw);
  if (['ναι', 'yes', 'true', '1'].includes(s)) return true;
  if (['οχι', 'no', 'false', '0'].includes(s)) return false;
  throw new Error(`Μη έγκυρη τιμή ΝΑΙ/ΟΧΙ: "${raw}"`);
}

export function parseNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const raw = cellToString(value);
  if (!raw) return undefined;
  const n = Number(String(raw).replace(',', '.'));
  if (!Number.isFinite(n)) throw new Error(`Μη έγκυρος αριθμός: "${raw}"`);
  return n;
}

/** Αναζήτηση σημείων με ακριβή επωνυμία (στο ορατό σύνολο του χρήστη) */
export async function findCollectionpointsByName(
  client: ApolloClient<object>,
  name: string,
): Promise<{ id: number; name: string }[]> {
  const ret = await client.query({
    query: gql`
      query getCollectionpoints($where: String, $limit: Int) {
        getCollectionpoints(where: $where, limit: $limit) {
          recordCount
          data {
            id
            name
          }
        }
      }
    `,
    variables: { where: JSON.stringify({ name }), limit: 20 },
    fetchPolicy: 'network-only',
  });
  const data = ret?.data?.getCollectionpoints?.data;
  return Array.isArray(data) ? data : [];
}

/**
 * Συνδέει το σημείο με τον συλλέκτη αν δεν υπάρχει ήδη η σχέση.
 * Καλείται μόνο όταν ο συνδεδεμένος χρήστης είναι collector.
 */
export async function ensureCollectorCollectionpointLink(
  client: ApolloClient<object>,
  collectorId: number,
  collectionpointId: number,
): Promise<'exists' | 'created'> {
  const existing = await client.query({
    query: gql`
      query getCollectorsCollectionpoints($where: String) {
        getCollectorsCollectionpoints(where: $where) {
          recordCount
          data {
            id
          }
        }
      }
    `,
    variables: { where: JSON.stringify({ collectorId, collectionpointId }) },
    fetchPolicy: 'network-only',
  });

  if ((existing?.data?.getCollectorsCollectionpoints?.recordCount ?? 0) > 0) {
    return 'exists';
  }

  await client.mutate({
    mutation: gql`
      mutation addCollectorsCollectionpoint($data: CollectorsCollectionpointInput) {
        addCollectorsCollectionpoint(data: $data) {
          id
        }
      }
    `,
    variables: {
      data: { collectorId, collectionpointId, isActive: true },
    },
  });
  return 'created';
}
